import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    PieChart,
    Pie,
    Cell,
    ChartTooltip,
    ResponsiveContainer,
    MuiTable,
    MuiTableContainer,
    MuiTableHead,
    MuiTableBody,
    MuiTableRow,
    Grid,
    Divider,
    Stack,
} from '../../../../base';
import {
    IClientTimeToHireJobData,
    IChartTooltipProps,
    IPieLabelProps,
    TIME_TO_HIRE_METRIC_KEYS,
    TIME_TO_HIRE_METRIC_LABELS,
    TIME_TO_HIRE_PIE_COLORS,
    RADIAN,
    exportJobMetricsToExcel,
} from '../../../../common';
import {
    StyledChartCard,
    StyledLegendItem,
    StyledColorDot,
    StyledTableCard,
    StyledTableHeaderCell,
    StyledTableBodyCell,
} from './JobSpecificView.styles';

interface IJobPieChartProps {
    data: IClientTimeToHireJobData;
}

const JobPieChart = ({ data }: IJobPieChartProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const pieData = TIME_TO_HIRE_METRIC_KEYS.map((key) => ({
        name: t(TIME_TO_HIRE_METRIC_LABELS[key]),
        value: Math.round(data[key].averageDays || 0),
        color: TIME_TO_HIRE_PIE_COLORS[key],
    }));

    const totalDays = pieData.reduce((sum, item) => sum + item.value, 0);

    const getPercentage = (days: number) =>
        totalDays > 0 ? Math.round((days / totalDays) * 100) : 0;

    const renderPercentLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        percent,
    }: IPieLabelProps) => {
        const radius = outerRadius * 0.65;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        if (percent < 0.05) return null;

        return (
            <text
                x={x}
                y={y}
                fill={theme.palette.common.white}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={600}
            >
                {`${Math.round(percent * 100)}%`}
            </text>
        );
    };

    const renderTooltipContent = ({ active, payload }: IChartTooltipProps) => {
        if (!active || !payload?.length) return null;
        const entry = payload[0];
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1.5),
                    borderRadius: 1.5,
                }}
            >
                {`${entry.name}: ${Math.round(entry.value)} ${t('reportsPage.timeToHireReport.days')}`}
            </Box>
        );
    };

    return (
        <Grid container gap={4} alignItems="flex-start">
            <StyledChartCard item xs="auto">
                <Box p={2}>
                    <Typography variant="body2" color={theme.palette.grey[600]}>
                        {`${t('reportsPage.timeToHireReport.jobSpecific.pieChartTitle')} - ${t('reportsPage.timeToHireReport.jobSpecific.totalHiringTime')} - ${totalDays} ${t('reportsPage.timeToHireReport.days')}`}
                    </Typography>
                </Box>
                <Divider sx={{ borderColor: theme.palette.grey[300] }} />
                <Grid container p={2}>
                    <Grid>
                        <ResponsiveContainer width={250} height={200}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    dataKey="value"
                                    stroke="none"
                                    label={renderPercentLabel}
                                    labelLine={false}
                                >
                                    {pieData.map((entry) => (
                                        <Cell
                                            key={entry.name}
                                            fill={entry.color}
                                        />
                                    ))}
                                </Pie>
                                <ChartTooltip
                                    content={renderTooltipContent as never}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Stack spacing={1} justifyContent={'center'}>
                        {pieData.map((entry) => (
                            <StyledLegendItem key={entry.name}>
                                <StyledColorDot dotColor={entry.color} />
                                <Typography variant="body2">
                                    {`${entry.name} (${entry.value} ${t('reportsPage.timeToHireReport.days')})`}
                                </Typography>
                            </StyledLegendItem>
                        ))}
                    </Stack>
                </Grid>
            </StyledChartCard>
            <StyledTableCard item>
                <MuiTableContainer>
                    <MuiTable>
                        <MuiTableHead>
                            <MuiTableRow>
                                <StyledTableHeaderCell>
                                    {t(
                                        'reportsPage.timeToHireReport.jobSpecific.tableHeaderJob'
                                    )}
                                </StyledTableHeaderCell>
                                <StyledTableHeaderCell>
                                    {t(
                                        'reportsPage.timeToHireReport.jobSpecific.tableHeaderStep'
                                    )}
                                </StyledTableHeaderCell>
                                <StyledTableHeaderCell>
                                    {t(
                                        'reportsPage.timeToHireReport.jobSpecific.tableHeaderAverageTime'
                                    )}
                                </StyledTableHeaderCell>
                            </MuiTableRow>
                        </MuiTableHead>
                        <MuiTableBody>
                            {pieData.map((entry) => (
                                <MuiTableRow key={entry.name}>
                                    <StyledTableBodyCell>
                                        {data.jobTitle}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell>
                                        {entry.name}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell>
                                        {`${entry.value} ${t('reportsPage.timeToHireReport.days')} (${getPercentage(entry.value)}%)`}
                                    </StyledTableBodyCell>
                                </MuiTableRow>
                            ))}
                        </MuiTableBody>
                    </MuiTable>
                </MuiTableContainer>
            </StyledTableCard>
            <Grid item xs="auto" sx={{ marginInlineStart: 'auto' }}>
                <IconButton
                    size="small"
                    onClick={() => exportJobMetricsToExcel(data, t)}
                >
                    <Icon name="FileDownloadOutlinedIcon" size={16} />
                </IconButton>
            </Grid>
        </Grid>
    );
};

export default JobPieChart;
